import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Outlet,
	useNavigate,
	useParams,
	useRouterState,
	useSearch,
} from "@tanstack/react-router";
import { useContext, useEffect } from "react";
import z from "zod";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useTypeAccessEditAllowed } from "@/hooks/use-type-access-edit-allowed";
import {
	useDisableRecordType,
	useEnableRecordType,
	useUpdateRecordType,
} from "@/services/record/record.mutations";
import { recordQueryOptions } from "@/services/record/record.options";
import { getRecordTypeDisplayName } from "@/utils/record-type-detail/record-type-detail.utils";
import { RecordTypeDrawerContext } from "@/utils/record-type-detail/record-type-drawer-context";
import { showSuccessToast } from "@/utils/toast.utils";
import type { OrgRecordTypeFullPageTab } from "./OrgRecordTypeFullPage";
import { OrgRecordTypeFullPage } from "./OrgRecordTypeFullPage";

const SearchSchema = z.object({
	tab: z
		.enum(["details", "access", "form", "rules", "activity"])
		.optional()
		.default("details"),
	mode: z.enum(["view", "edit"]).optional().default("view"),
	presentation: z.enum(["drawer", "modal", "full"]).optional().default("full"),
	ruleId: z.string().optional(),
	/** Editor open mode for the rule drawer on `/full/form/rules`. */
	ruleMode: z.enum(["create", "edit", "delete"]).optional(),
	/** Sub-tab inside a single rule's drawer/full view. */
	ruleTab: z.enum(["details", "activity"]).optional(),
	/** Sub-org scope for record-type form definitions (preserved across preview ↔ editor navigation). */
	subOrganizationId: z.string().optional(),
});

const FULL_PARENT =
	"/org-admin/$orgId/configuration/recordTypes/$recordTypeId/full" as const;

export const Route = createFileRoute(
	"/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/configuration/recordTypes/$recordTypeId/full",
)({
	component: OrgRecordTypeFullView,
	validateSearch: (search) => SearchSchema.parse(search),
	params: {
		parse: (raw) => {
			const p = raw as { orgId: string; recordTypeId: string };
			return {
				orgId: z.string().parse(p.orgId),
				recordTypeId: z.string().parse(p.recordTypeId),
			};
		},
	},
});

function OrgRecordTypeFullView() {
	const navigate = useNavigate();
	const { t } = useAppTranslation();
	const params = useParams({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/configuration/recordTypes/$recordTypeId/full",
	});
	const search = useSearch({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/configuration/recordTypes/$recordTypeId/full",
	});
	const organizationIdForApi = params.orgId;
	const pathname = useRouterState({ select: (s) => s.location.pathname });

	const detailQuery = useQuery(
		recordQueryOptions.recordTypeDetail(params.recordTypeId, {
			organizationId: organizationIdForApi,
		}),
	);
	const recordType = detailQuery.data ?? null;
	const drawerCtx = useContext(RecordTypeDrawerContext);
	const accessEdit = useTypeAccessEditAllowed(
		"RecordType",
		params.recordTypeId,
	);
	const updateMutation = useUpdateRecordType();
	const enableMutation = useEnableRecordType();
	const disableMutation = useDisableRecordType();
	const isSavingAny =
		updateMutation.isPending ||
		enableMutation.isPending ||
		disableMutation.isPending;

	const formSegment = `/configuration/recordTypes/${params.recordTypeId}/full/form`;
	const onFormRoutes = pathname.includes(formSegment);
	const rulesSegment = `/configuration/recordTypes/${params.recordTypeId}/full/rules/`;
	const onRuleDetailRoute = pathname.includes(rulesSegment);

	const activeTab: OrgRecordTypeFullPageTab = onFormRoutes
		? "form"
		: search.tab;

	useEffect(() => {
		if (search.mode !== "edit" || search.tab !== "access") return;
		if (accessEdit.isPending || accessEdit.allowed) return;
		void navigate({
			to: FULL_PARENT,
			params: { orgId: params.orgId, recordTypeId: params.recordTypeId },
			search: {
				tab: search.tab,
				mode: "view",
				presentation: search.presentation,
			},
		});
	}, [
		accessEdit.allowed,
		accessEdit.isPending,
		navigate,
		params.orgId,
		params.recordTypeId,
		search.mode,
		search.presentation,
		search.tab,
	]);

	if (onRuleDetailRoute || onFormRoutes) {
		return <Outlet />;
	}

	const isEditMode =
		search.mode === "edit" &&
		(search.tab === "access"
			? accessEdit.allowed
			: (drawerCtx?.canEdit ?? false));

	const handleTabChange = (tab: OrgRecordTypeFullPageTab) => {
		if (tab === "form") {
			void navigate({
				to: FULL_PARENT,
				params: { orgId: params.orgId, recordTypeId: params.recordTypeId },
				search: {
					tab: "form",
					mode: search.mode,
					presentation: search.presentation,
					subOrganizationId: search.subOrganizationId,
				},
			});
			return;
		}
		void navigate({
			to: FULL_PARENT,
			params: { orgId: params.orgId, recordTypeId: params.recordTypeId },
			search: { tab, mode: search.mode, presentation: search.presentation },
		});
	};

	const handleBackToList = () => {
		void navigate({
			to: "/org-admin/$orgId/configuration/recordTypes",
			params: { orgId: params.orgId },
		});
	};

	if (!recordType && !detailQuery.isLoading && !detailQuery.isError) {
		return null;
	}

	if (!recordType) {
		return (
			<div className="flex min-h-screen items-center justify-center">
				<div className="text-sm text-gray-500">
					{detailQuery.isLoading
						? t("common.actions.loadingEllipsis")
						: t("admin.recordTypes.drawer.errors.notFound")}
				</div>
			</div>
		);
	}

	const fullPageContext = drawerCtx
		? {
				...drawerCtx,
				mode: (search.mode ?? "view") as "view" | "edit",
				setMode: (mode: "view" | "edit") => {
					void navigate({
						to: FULL_PARENT,
						params: { orgId: params.orgId, recordTypeId: params.recordTypeId },
						search: {
							tab: search.tab,
							mode,
							presentation: search.presentation,
						},
					});
				},
				isSaving: isSavingAny,
				saveDraft: async () => {
					if (!drawerCtx.isValid || isSavingAny) return;
					if (
						search.tab === "access"
							? !accessEdit.allowed
							: !drawerCtx.canEdit
					)
						return;
					try {
						if (search.tab === "access") {
							if (drawerCtx.draftSubOrgIds === null) {
								await enableMutation.mutateAsync({
									id: params.recordTypeId,
									organizationId: organizationIdForApi,
								});
							} else if (drawerCtx.draftSubOrgIds.length === 0) {
								await disableMutation.mutateAsync({
									id: params.recordTypeId,
									organizationId: organizationIdForApi,
								});
							} else {
								await updateMutation.mutateAsync({
									id: params.recordTypeId,
									data: {
										name: drawerCtx.draftName.trim(),
										description: drawerCtx.draftDescription || null,
										icon: drawerCtx.draftIcon || null,
										subOrganizationIds: drawerCtx.draftSubOrgIds,
										locationRequired: drawerCtx.locationRequired,
									},
									organizationId: organizationIdForApi,
								});
							}
							showSuccessToast({
								message: t("admin.recordTypes.drawer.access.toast.saveSuccess"),
							});
						} else {
							// Details save: preserve the current sub-org list
							const currentSubOrgIds = (
								recordType.recordTypeSubOrganizations ?? []
							)
								.map((s) => s.id)
								.filter((id): id is string => id !== null);
							await updateMutation.mutateAsync({
								id: params.recordTypeId,
								data: {
									name: drawerCtx.draftName.trim(),
									description: drawerCtx.draftDescription || null,
									icon: drawerCtx.draftIcon || null,
									subOrganizationIds:
										currentSubOrgIds.length > 0 ? currentSubOrgIds : null,
									locationRequired: drawerCtx.locationRequired,
								},
								organizationId: organizationIdForApi,
							});
							drawerCtx.setDraftName(drawerCtx.draftName.trim());
							showSuccessToast({
								message: t(
									"admin.recordTypes.drawer.details.toast.saveSuccess",
								),
							});
						}
						void navigate({
							to: FULL_PARENT,
							params: {
								orgId: params.orgId,
								recordTypeId: params.recordTypeId,
							},
							search: {
								tab: search.tab,
								mode: "view" as const,
								presentation: search.presentation,
							},
						});
					} catch {
						// Global mutation error handler already shows the error toast
					}
				},
				cancelDraft: () => {
					const name = getRecordTypeDisplayName(recordType);
					drawerCtx.setDraftName(name);
					drawerCtx.setDraftDescription(recordType.description ?? "");
					drawerCtx.setDraftIcon(recordType.icon ?? "");
					const subOrgIds = (recordType.recordTypeSubOrganizations ?? [])
						.map((s) => s.id)
						.filter((id): id is string => id !== null);
					drawerCtx.setDraftSubOrgIds(
						subOrgIds.length > 0
							? subOrgIds
							: recordType.enabled === false
								? []
								: null,
					);
					void navigate({
						to: FULL_PARENT,
						params: { orgId: params.orgId, recordTypeId: params.recordTypeId },
						search: {
							tab: search.tab,
							mode: "view" as const,
							presentation: search.presentation,
						},
					});
				},
			}
		: null;

	return (
		<RecordTypeDrawerContext.Provider value={fullPageContext}>
			<OrgRecordTypeFullPage
				recordType={recordType}
				orgId={params.orgId}
				activeTab={activeTab}
				onTabChange={handleTabChange}
				onBackToList={handleBackToList}
				isLoading={detailQuery.isLoading}
				isError={detailQuery.isError}
				isEditMode={isEditMode}
			>
				<Outlet />
			</OrgRecordTypeFullPage>
		</RecordTypeDrawerContext.Provider>
	);
}
